import { useState, type FormEvent } from 'react';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@ludolio/shared-ui';
import { Activity as ActivityIcon, CheckCircle2, Copy, Eye, LibraryBig, Loader2, Pencil, Plus, Share2, Trash2, X } from 'lucide-react';
import { useUnitsModule } from '../../module-context';
import type { Achievement, Activity, ActivityLibraryItem, ActivityQuestion, ActivityResponse, ActivityType, Game } from '../../types';

interface ActivitiesTabProps {
  unitId: string;
  games: Game[];
  activities: Activity[];
  onDataChanged(): Promise<void>;
}

interface EditableQuestion {
  question: string;
  options: string[];
  correctAnswers: string[];
}

const emptyQuestion = (): EditableQuestion => ({ question: '', options: ['', ''], correctAnswers: [] });

function getStudentName(student?: ActivityResponse['student']) {
  if (!student) return 'Unknown';
  return student.name || [student.firstName, student.lastName].filter(Boolean).join(' ') || student.email || 'Unknown';
}

export function ActivitiesTab({ unitId, games, activities, onDataChanged }: ActivitiesTabProps) {
  const { api, capabilities, reportError } = useUnitsModule();
  const [editorOpen, setEditorOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryItems, setLibraryItems] = useState<ActivityLibraryItem[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [responsesOpen, setResponsesOpen] = useState(false);
  const [responses, setResponses] = useState<ActivityResponse[]>([]);
  const [responsesTitle, setResponsesTitle] = useState('');
  const [gameId, setGameId] = useState('');
  const [achievementId, setAchievementId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<ActivityType>('short_answer');
  const [questions, setQuestions] = useState<EditableQuestion[]>([emptyQuestion()]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);

  const resetEditor = () => {
    setEditingId(null);
    setGameId('');
    setAchievementId('');
    setTitle('');
    setDescription('');
    setType('short_answer');
    setQuestions([emptyQuestion()]);
    setAchievements([]);
  };

  const loadLibrary = async () => {
    if (!capabilities.canManageActivities) return;
    try {
      setLibraryLoading(true);
      const response = await api.listActivityLibrary();
      setLibraryItems(response.success && response.data ? response.data : []);
    } catch (error) {
      console.error('Failed to load activity library:', error);
      reportError(error);
      setLibraryItems([]);
    } finally {
      setLibraryLoading(false);
    }
  };

  const importItem = async (item: ActivityLibraryItem) => {
    if (!capabilities.canManageActivities) return;
    try {
      setImportingId(item.id);
      const response = await api.importActivityToUnit(item.id, unitId);
      if (!response.success) throw new Error(response.error || 'Failed to import activity');
      setLibraryOpen(false);
      await onDataChanged();
    } catch (error) {
      console.error('Failed to import activity:', error);
      reportError(error);
      alert(error instanceof Error ? error.message : 'Failed to import activity');
    } finally {
      setImportingId(null);
    }
  };

  const selectGame = async (selectedId: string) => {
    setGameId(selectedId);
    setAchievementId('');
    try {
      const response = await api.getGameAchievements(selectedId);
      setAchievements(response.success && response.data ? response.data : []);
    } catch {
      setAchievements([]);
    }
  };

  const saveActivity = async (event: FormEvent) => {
    event.preventDefault();
    if (!capabilities.canManageActivities) return;
    const configQuestions: ActivityQuestion[] = questions.map((question) => {
      if (type !== 'quiz') return { question: question.question };
      const options = question.options.filter((option) => option.trim());
      const validOptions = new Set(options);
      const correctAnswers = question.correctAnswers.filter((answer) => validOptions.has(answer));
      return { question: question.question, options, ...(correctAnswers.length ? { correctAnswers } : {}) };
    });
    try {
      if (editingId) {
        await api.updateActivity({ activityId: editingId, title, description: description || undefined, type, config: { questions: configQuestions } });
      } else {
        await api.createActivity({ unitId, gameId, achievementId, title, description: description || undefined, type, config: { questions: configQuestions } });
      }
      setEditorOpen(false);
      resetEditor();
      await onDataChanged();
    } catch (error) {
      console.error('Failed to save activity:', error);
      reportError(error);
    }
  };

  const editActivity = (activity: Activity) => {
    if (!capabilities.canManageActivities) return;
    setEditingId(activity.id);
    setTitle(activity.title);
    setDescription(activity.description || '');
    setType(activity.type);
    setQuestions(activity.config.questions.length ? activity.config.questions.map((question) => ({
      question: question.question || '',
      options: question.options || ['', ''],
      correctAnswers: question.correctAnswers || [],
    })) : [emptyQuestion()]);
    setEditorOpen(true);
  };

  const shareActivity = async (activity: Activity) => {
    if (!capabilities.canManageActivities) return;
    try {
      const response = await api.createActivityLibraryItem({
        gameId: activity.gameId,
        achievementId: activity.achievementId,
        title: activity.title,
        description: activity.description || undefined,
        type: activity.type,
        config: activity.config,
        visibility: 'org',
      });
      if (!response.success) throw new Error(response.error || 'Failed to save activity to library');
      alert('Activity saved to the organization library.');
    } catch (error) {
      console.error('Failed to save activity to library:', error);
      reportError(error);
    }
  };

  const viewResponses = async (activity: Activity) => {
    if (!capabilities.canViewActivityResponses) return;
    setResponsesTitle(activity.title);
    try {
      const response = await api.getActivityResponses(activity.id);
      setResponses(response.success && response.data ? response.data : []);
    } catch {
      setResponses([]);
    }
    setResponsesOpen(true);
  };

  const removeActivity = async (activityId: string) => {
    if (!capabilities.canManageActivities || !confirm('Delete this activity?')) return;
    try {
      await api.deleteActivity(activityId);
      await onDataChanged();
    } catch (error) {
      console.error('Failed to delete activity:', error);
      reportError(error);
    }
  };

  const updateOption = (questionIndex: number, optionIndex: number, next: string) => {
    setQuestions((current) => current.map((question, index) => {
      if (index !== questionIndex) return question;
      const previous = question.options[optionIndex]?.trim() || '';
      const options = [...question.options];
      options[optionIndex] = next;
      const correctAnswers = previous
        ? question.correctAnswers.map((answer) => answer === previous ? next.trim() : answer).filter(Boolean)
        : question.correctAnswers;
      return { ...question, options, correctAnswers };
    }));
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    setQuestions((current) => current.map((question, index) => {
      if (index !== questionIndex) return question;
      const removed = question.options[optionIndex]?.trim();
      return {
        ...question,
        options: question.options.filter((_, index) => index !== optionIndex),
        correctAnswers: removed ? question.correctAnswers.filter((answer) => answer !== removed) : question.correctAnswers,
      };
    }));
  };

  return (
    <>
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold">Activities ({activities.length})</h3>
        {capabilities.canManageActivities && (
          <div className="flex flex-wrap gap-2">
            <Dialog open={libraryOpen} onOpenChange={(open) => { setLibraryOpen(open); if (open) void loadLibrary(); }}>
              <DialogTrigger asChild><Button variant="outline" size="sm"><LibraryBig className="mr-2 h-4 w-4" />Import from Library</Button></DialogTrigger>
              <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Import from Activity Library</DialogTitle>
                  <DialogDescription>Copy a reusable template into this unit. The unit must already include the template’s game.</DialogDescription>
                </DialogHeader>
                {libraryLoading ? <div className="py-8 text-center text-muted-foreground">Loading library...</div>
                  : libraryItems.length === 0 ? <div className="py-8 text-center text-muted-foreground">No library activities available yet.</div>
                  : <div className="space-y-3">{libraryItems.map((item) => {
                      const hasGame = games.some((game) => game.id === item.gameId);
                      return <Card key={item.id}><CardContent className="p-4"><div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <div className="font-medium">{item.title}</div>
                          <div className="text-sm text-muted-foreground">{item.description || 'No description provided.'}</div>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline">{item.game?.title || 'Unknown game'}</Badge>
                            <Badge variant="outline">{item.achievement?.name || 'Unknown achievement'}</Badge>
                            <Badge variant={item.visibility === 'org' ? 'default' : 'secondary'}>{item.visibility === 'org' ? 'Org shared' : item.visibility}</Badge>
                            {!hasGame && <Badge variant="destructive">Game not in unit</Badge>}
                          </div>
                        </div>
                        <Button size="sm" disabled={!hasGame || importingId === item.id} onClick={() => void importItem(item)}>
                          {importingId === item.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />}Import
                        </Button>
                      </div></CardContent></Card>;
                    })}</div>}
              </DialogContent>
            </Dialog>
            <Dialog open={editorOpen} onOpenChange={(open) => { setEditorOpen(open); if (!open) resetEditor(); }}>
              <DialogTrigger asChild><Button size="sm"><Plus className="mr-2 h-4 w-4" />Create Activity</Button></DialogTrigger>
              <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingId ? 'Edit Activity' : 'Create Activity'}</DialogTitle>
                  <DialogDescription>{editingId ? 'Update the activity details below.' : 'Link an activity to a game achievement. Students will see it once they unlock the achievement.'}</DialogDescription>
                </DialogHeader>
                <form onSubmit={saveActivity} className="space-y-4">
                  {!editingId && <>
                    <div className="space-y-2"><Label>Game</Label><Select value={gameId} onValueChange={(value) => void selectGame(value)}>
                      <SelectTrigger><SelectValue placeholder="Select a game" /></SelectTrigger>
                      <SelectContent>{games.map((game) => <SelectItem key={game.id} value={game.id}>{game.title}</SelectItem>)}</SelectContent>
                    </Select></div>
                    {gameId && <div className="space-y-2"><Label>Achievement (trigger)</Label><Select value={achievementId} onValueChange={setAchievementId}>
                      <SelectTrigger><SelectValue placeholder="Select an achievement" /></SelectTrigger>
                      <SelectContent>{achievements.map((achievement) => <SelectItem key={achievement.id} value={achievement.id}>{achievement.name}</SelectItem>)}</SelectContent>
                    </Select></div>}
                  </>}
                  <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Activity title" required /></div>
                  <div className="space-y-2"><Label>Description (optional)</Label><Textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Brief description" /></div>
                  <div className="space-y-2"><Label>Type</Label><Select value={type} onValueChange={(value) => setType(value as ActivityType)}>
                    <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="short_answer">Short Answer</SelectItem><SelectItem value="quiz">Quiz</SelectItem></SelectContent>
                  </Select></div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between"><Label>Questions</Label><Button type="button" variant="outline" size="sm" onClick={() => setQuestions((current) => [...current, emptyQuestion()])}><Plus className="mr-1 h-4 w-4" />Add Question</Button></div>
                    {questions.map((question, questionIndex) => <div key={questionIndex} className="space-y-3 rounded-md border p-3">
                      <div className="flex items-center justify-between"><span className="text-sm font-medium">Question {questionIndex + 1}</span>
                        {questions.length > 1 && <Button type="button" variant="ghost" size="sm" aria-label={`Remove question ${questionIndex + 1}`} onClick={() => setQuestions((current) => current.filter((_, index) => index !== questionIndex))}><X className="h-4 w-4" /></Button>}
                      </div>
                      <Textarea value={question.question} onChange={(event) => setQuestions((current) => current.map((item, index) => index === questionIndex ? { ...item, question: event.target.value } : item))} placeholder="Enter your question" required />
                      {type === 'quiz' && <div className="space-y-2">
                        <div className="flex items-center justify-between"><span className="text-xs font-medium text-muted-foreground">Options</span><span className="text-xs text-muted-foreground">Check to mark correct</span></div>
                        {question.options.map((option, optionIndex) => <div key={optionIndex} className="flex items-center gap-2">
                          <Checkbox checked={option.trim() !== '' && question.correctAnswers.includes(option.trim())} disabled={!option.trim()} onCheckedChange={(checked) => {
                            const value = option.trim();
                            if (!value) return;
                            setQuestions((current) => current.map((item, index) => index === questionIndex ? { ...item, correctAnswers: checked ? [...item.correctAnswers.filter((answer) => answer !== value), value] : item.correctAnswers.filter((answer) => answer !== value) } : item));
                          }} />
                          <Input value={option} onChange={(event) => updateOption(questionIndex, optionIndex, event.target.value)} placeholder={`Option ${optionIndex + 1}`} />
                          {question.options.length > 2 && <Button type="button" variant="ghost" size="sm" aria-label={`Remove option ${optionIndex + 1}`} onClick={() => removeOption(questionIndex, optionIndex)}><X className="h-4 w-4" /></Button>}
                        </div>)}
                        <Button type="button" variant="outline" size="sm" onClick={() => setQuestions((current) => current.map((item, index) => index === questionIndex ? { ...item, options: [...item.options, ''] } : item))}><Plus className="mr-1 h-4 w-4" />Add Option</Button>
                      </div>}
                    </div>)}
                  </div>
                  <Button type="submit" className="w-full" disabled={(!editingId && (!gameId || !achievementId)) || !title || questions.some((question) => !question.question.trim())}>{editingId ? 'Save Changes' : 'Create Activity'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {activities.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ActivityIcon className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">No Activities Yet</h3>
            <p className="text-muted-foreground">
              {capabilities.canManageActivities
                ? 'Create an activity linked to a game achievement to get started.'
                : 'No activities are currently assigned to this unit.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <Card key={activity.id}>
              <CardHeader className="pb-2">
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
                  <div className="min-w-0">
                    <CardTitle className="text-base">{activity.title}</CardTitle>
                    <CardDescription>
                      {activity.game?.title} — {activity.achievement?.name} ·{' '}
                      <Badge variant={activity.type === 'quiz' ? 'default' : 'secondary'}>
                        {activity.type === 'quiz' ? 'Quiz' : 'Short Answer'}
                      </Badge>
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {capabilities.canManageActivities && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => void shareActivity(activity)}>
                          <Share2 className="mr-1 h-4 w-4" />Share
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => editActivity(activity)}>
                          <Pencil className="mr-1 h-4 w-4" />Edit
                        </Button>
                      </>
                    )}
                    {capabilities.canViewActivityResponses && (
                      <Button variant="outline" size="sm" onClick={() => void viewResponses(activity)}>
                        <Eye className="mr-1 h-4 w-4" />Responses ({activity.responses?.length || 0})
                      </Button>
                    )}
                    {capabilities.canManageActivities && (
                      <Button variant="destructive" size="sm" aria-label={`Delete ${activity.title}`} title={`Delete ${activity.title}`} onClick={() => void removeActivity(activity.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {activity.config.questions.length > 0 ? (
                  <div className="space-y-2">
                    {activity.config.questions.map((question, questionIndex) => (
                      <div key={questionIndex}>
                        <p className="mb-1 text-sm text-muted-foreground">
                          <strong>Q{activity.config.questions.length > 1 ? questionIndex + 1 : ''}:</strong> {question.question}
                        </p>
                        {question.options && question.options.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-2">
                            {question.options.map((option, optionIndex) => {
                              const isCorrect = question.correctAnswers?.includes(option);
                              return (
                                <Badge key={optionIndex} variant={isCorrect ? 'success' : 'outline'} className="flex items-center gap-1">
                                  {isCorrect && <CheckCircle2 className="h-3 w-3" />}
                                  {option}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mb-1 text-sm text-muted-foreground">No questions configured</p>
                )}
                {activity.description && <p className="mt-2 text-xs text-muted-foreground">{activity.description}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={responsesOpen} onOpenChange={setResponsesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Responses — {responsesTitle}</DialogTitle>
            <DialogDescription>{responses.length} response(s)</DialogDescription>
          </DialogHeader>
          {responses.length === 0 ? (
            <p className="py-4 text-center text-muted-foreground">No responses yet.</p>
          ) : (
            <div className="max-h-96 space-y-3 overflow-y-auto">
              {responses.map((response) => (
                <Card key={response.id}>
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium">{getStudentName(response.student)}</p>
                        <div className="mt-1 space-y-1 text-sm">
                          {(response.response?.answers?.length
                            ? response.response.answers.map((answer) => answer.answer)
                            : [response.response?.answer || 'No answer provided']
                          ).map((answer, index) => <p key={`${response.id}-${index}`}>{answer}</p>)}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(response.submittedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
